import { hasSelectedEntity } from '../../../../shared/utils/entitySelection';
import { isPastDueDate } from '../../../../shared/utils/overdue';
import { INITIAL_PROPOSAL_FILTERS, PROPOSAL_PRIORITY_META } from './proposals.constants';

export function getDefaultProposalFilters() {
  return { ...INITIAL_PROPOSAL_FILTERS };
}

export function hasActiveProposalFilters(filters) {
  return (
    filters.searchTerm.trim() !== '' ||
    filters.priorityFilter !== 'all' ||
    filters.assigneeFilter !== 'all' ||
    filters.stageFilter !== 'all' ||
    (filters.clientFilter && filters.clientFilter !== 'all')
  );
}

export function getPriorityBadgeClass(priority) {
  return (
    PROPOSAL_PRIORITY_META[priority]?.badgeClass ||
    'border-[var(--border)] text-[var(--text-primary)] bg-[var(--surface-raised)]'
  );
}

export function formatProposalDate(dateString) {
  if (!dateString) return '—';

  return new Date(dateString).toLocaleDateString('it-IT');
}

export function normalizeSearchValue(value) {
  return String(value || '').trim().toLowerCase();
}

export function filterProposals(proposals, filters) {
  const search = normalizeSearchValue(filters.searchTerm);

  return proposals.filter((proposal) => {
    const matchesSearch =
      !search ||
      normalizeSearchValue(proposal.name).includes(search) ||
      normalizeSearchValue(proposal.client).includes(search) ||
      normalizeSearchValue(proposal.assignee).includes(search) ||
      normalizeSearchValue(proposal.tipoAffare).includes(search);

    const matchesPriority =
      filters.priorityFilter === 'all' ||
      proposal.priority === filters.priorityFilter;

    const matchesAssignee =
      filters.assigneeFilter === 'all' ||
      (proposal.assignee || '').split('; ').includes(filters.assigneeFilter);

    const matchesStage =
      filters.stageFilter === 'all' ||
      proposal.stage === filters.stageFilter;

    const matchesClient =
      !filters.clientFilter ||
      filters.clientFilter === 'all' ||
      hasSelectedEntity(proposal, 'client', filters.clientFilter);

    return matchesSearch && matchesPriority && matchesAssignee && matchesStage && matchesClient;
  });
}

export function parseCurrencyValue(value) {
  if (typeof value === 'number') return value;
  if (!value) return 0;

  const cleaned = String(value)
    .replace(/[€\s]/g, '')
    .replace(/\./g, '')
    .replace(',', '.');

  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
}

function parseDateValue(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function getMostRecentDate(values = []) {
  return values
    .map(parseDateValue)
    .filter(Boolean)
    .sort((left, right) => right.getTime() - left.getTime())[0] || null;
}

export function getLastProposalActivityDate(proposal = {}) {
  const directDate = getMostRecentDate([proposal.updatedAt, proposal.lastActivityAt]);
  if (directDate) return directDate;

  const nestedDates = [
    ...(proposal.comments || []),
    ...(proposal.replies || []),
    ...(proposal.responses || []),
  ].flatMap((item) => [item?.updatedAt, item?.createdAt, item?.date, item?.timestamp]);
  const nestedDate = getMostRecentDate(nestedDates);
  if (nestedDate) return nestedDate;

  return getMostRecentDate([proposal.proposalDate, proposal.dataInizio]);
}

export function isProposalOverdue(proposal = {}, now = new Date()) {
  if (proposal.inProgressDealId || ['annullato', 'annullata', 'rifiutato', 'rifiutata', 'completato', 'completata', 'cancelled', 'completed'].includes(proposal.status)) return false;
  return [proposal.dueDate, proposal.paymentExpectedDate, proposal.dataPagamentoPrevista,
    ...(proposal.installmentDueDates || []), ...(proposal.dilazionamento?.scadenze || [])]
    .some((date) => isPastDueDate(date, now));
}

export function filterProposalsByOverview(proposals, overviewFilter) {
  if (!overviewFilter || overviewFilter === 'total') return proposals;
  if (overviewFilter === 'highPriority') {
    return proposals.filter((proposal) => proposal.priority === 'alta');
  }
  if (overviewFilter === 'overdue') {
    return proposals.filter((proposal) => isProposalOverdue(proposal));
  }
  if (overviewFilter === 'pipelineVolume') {
    return proposals.filter((proposal) => parseCurrencyValue(proposal.estimatedValue || proposal.value) > 0);
  }
  return proposals;
}

export function calculateProposalStats(proposals, proposalStages = []) {
  const total = proposals.length;
  const highPriority = proposals.filter((proposal) => proposal.priority === 'alta').length;
  const pipelineVolume = proposals.reduce((acc, proposal) => {
    return acc + parseCurrencyValue(proposal.estimatedValue || proposal.value);
  }, 0);
  const overdue = proposals.filter((proposal) => isProposalOverdue(proposal)).length;

  const activeStages = proposalStages.filter((stage) =>
    proposals.some((proposal) => proposal.stage === stage)
  ).length;

  return {
    total,
    highPriority,
    totalEstimatedValue: pipelineVolume,
    pipelineVolume,
    overdue,
    activeStages,
  };
}

export function createProposalPayload(proposal, existingProposals = []) {
  const nextId =
    Math.max(...existingProposals.map((item) => Number(item.id) || 0), 0) + 1;

  return {
    ...proposal,
    id: proposal.id || nextId,
    proposalDate:
      proposal.proposalDate || proposal.dataInizio || new Date().toISOString().split('T')[0],
    estimatedValue: proposal.estimatedValue || proposal.value || '',
  };
}

export function createProposalState(existingProposals = [], proposal) {
  const proposalPayload = createProposalPayload(proposal, existingProposals);

  return {
    proposals: [...existingProposals, proposalPayload],
    proposal: proposalPayload,
  };
}

export function saveProposalState(existingProposals = [], updatedProposal) {
  return {
    proposals: existingProposals.map((proposal) =>
      proposal.id === updatedProposal.id ? updatedProposal : proposal
    ),
    proposal: updatedProposal,
  };
}

export function removeProposalState(existingProposals = [], proposalId) {
  return {
    proposals: existingProposals.filter((proposal) => proposal.id !== proposalId),
  };
}

export function updateProposalStageState(existingProposals = [], proposalId, newStage) {
  let updatedProposal = null;

  const proposals = existingProposals.map((proposal) => {
    if (proposal.id !== proposalId) return proposal;

    updatedProposal = {
      ...proposal,
      stage: newStage,
    };

    return updatedProposal;
  });

  return {
    proposals,
    proposal: updatedProposal,
  };
}

export function getProposalMoveMessage(proposalName, destinationType) {
  const destinationLabel =
    destinationType === 'proposta'
      ? 'Proposte'
      : destinationType === 'inCorso'
        ? 'Affari in Corso'
        : 'Affari Completati';

  return `Proposta "${proposalName}" spostata in ${destinationLabel}`;
}

export function getStageCardCountLabel(count) {
  return `${count} ${count === 1 ? 'proposta' : 'proposte'}`;
}
